<i18n locale="en">
{
  "alignment": {
    "left": "Left",
    "right": "Right"
  },
  "labels": {
    "header": "Title",
    "body": "Subtitle",
    "action": "Button",
    "foreground": "Media",
    "background": "Background",
    "alignment": "Alignment",
    "layout": "Background Photo"
  },
  "foreground": {
    "image": "Photo",
    "none": "None",
    "video": "Video"
  },
  "background": {
    "none": "None",
    "image": "Photo",
    "color": "Color"
  },
  "colors": {
    "white": "White",
    "beige": "Beige",
    "gray": "Gray",
    "black": "Black",
    "brand-primary": "Brand Primary"
  }
}
</i18n>

<template>
  <div class="flex-col gap-8">
    <OwnInputContainer :label="t('labels.background')">
      <OwnSegmentedControl
        :options="backgroundOptions"
        :model-value="currentBlockParams.background?.type ?? 'none'"
        @update:model-value="changeBackgroundType"
      />
    </OwnInputContainer>
    <ContentImageEditor
      v-if="currentBlockParams.background?.type === 'image'"
      key="background-image-editor"
      control-name="backgroundImage"
      :layout="'cover'"
      :model-value="currentBlockParams.background?.params"
      @update="onBackgroundImageUpdate"
    />
    <OwnInputContainer
      v-if="currentBlockParams.background?.type === 'color'"
      :label="t('background.color')"
    >
      <div class="content-editor__color-tiles">
        <button
          v-for="token in backgroundColorOptions"
          :key="token"
          type="button"
          :aria-label="t(`colors.${token}`)"
          :class="[
            'content-editor__color-tile',
            currentBlockParams.background?.params?.token === token &&
              'content-editor__color-tile--selected',
          ]"
          :style="{ backgroundColor: tokenToCss(token) }"
          @click="updateBackgroundColor(token)"
        />
      </div>
    </OwnInputContainer>
    <OwnInputContainer :label="t('labels.foreground')">
      <OwnSegmentedControl
        :options="foregroundMediaOptions"
        :model-value="foregroundMedia?.type ?? 'none'"
        @update:model-value="changeMediaType"
      />
    </OwnInputContainer>
    <ContentImageEditor
      v-if="foregroundMedia?.type === 'image'"
      key="foreground-image-editor"
      control-name="foregroundImage"
      :layout="'generic'"
      :model-value="foregroundMedia.params"
      @update="
        onFormControlUpdate($event.controlName, $event.value, $event.status)
      "
    />
    <ContentVideoEditor
      v-if="foregroundMedia?.type === 'video'"
      :model-value="{ video: foregroundMedia.params?.params?.id }"
      @update="
        onFormControlUpdate($event.controlName, $event.value, $event.status)
      "
    />
    <OwnInputContainer
      v-if="
        foregroundMedia !== null ||
        currentBlockParams.background?.type === 'image'
      "
      :label="t('labels.alignment')"
    >
      <OwnSegmentedControl
        :options="alignmentOptions"
        :model-value="currentBlockParams.alignment"
        @update:model-value="applyContentUpdate({ alignment: $event })"
      />
    </OwnInputContainer>
    <OwnInputContainer
      :label="t('labels.header')"
      :errors="header.status.errors"
      :max-chars="websiteSectionMaxChars(50)"
      :value="header.value"
    >
      <OwnInput v-model="header.value" />
    </OwnInputContainer>
    <OwnInputContainer
      :label="t('labels.body')"
      :errors="body.status.errors"
      :max-chars="websiteSectionMaxChars(500)"
      :value="body.value"
    >
      <OwnTextarea v-model="body.value" />
    </OwnInputContainer>
    <ToggleCard
      :label="t(`labels.action`)"
      :model-value="currentBlockParams.action !== null"
      @update:model-value="toggleAction"
    >
      <ContentActionEditor
        v-if="currentBlockParams.action !== null"
        class="content-editor content-editor__action-inputs"
        :model-value="currentBlockParams.action"
        @update="applyActionOverride"
      />
    </ToggleCard>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n'
import { mapGetters } from 'vuex'

import ToggleCard from '@/components/cards/ToggleCard.vue'
import {
  websiteSectionMaxChars,
  WebsiteSectionValidators,
} from '@/features/website-builder/builder/validation/websiteSectionValidators'
import { FormBuilder } from '@/forms'
import { useExtendedMergedThemeMixin } from '@/lib/theme/useExtendedMergedTheme'
import { OwnInput } from '@/ui/OwnInput'
import { OwnInputContainer } from '@/ui/OwnInputContainer'
import { OwnSegmentedControl } from '@/ui/OwnSegmentedControl'
import { OwnTextarea } from '@/ui/OwnTextarea'
import { tokenToCss, BACKGROUND_COLOR_TOKENS } from '@/utils/backgroundColors'

import { ContentCommands } from '../commands/ContentCommands'

import ContentActionEditor from './ContentActionEditor.vue'
import ContentImageEditor from './ContentImageEditor.vue'
import ContentVideoEditor from './ContentVideoEditor.vue'

export default {
  name: 'ContentEditor',
  components: {
    ContentActionEditor,
    ContentImageEditor,
    ContentVideoEditor,
    OwnInput,
    OwnInputContainer,
    OwnSegmentedControl,
    OwnTextarea,
    ToggleCard,
  },
  mixins: [
    useExtendedMergedThemeMixin,
    FormBuilder({
      body: {
        debounce: 250,
        validateOnInit: true,
        validators: [
          WebsiteSectionValidators.required,
          WebsiteSectionValidators.maxLength(500),
        ],
      },
      header: {
        debounce: 250,
        validateOnInit: true,
        validators: [
          WebsiteSectionValidators.required,
          WebsiteSectionValidators.maxLength(50),
        ],
      },
    }),
  ],
  props: {
    /**
     * Id getting passed to the editor component
     */
    id: { type: String, required: true },
  },
  setup() {
    const { t } = useI18n()
    return { t, websiteSectionMaxChars }
  },
  data() {
    return {
      alignmentOptions: ['left', 'right'].map((alignmentOption) => ({
        label: this.t(`alignment.${alignmentOption}`),
        value: alignmentOption,
      })),
      backgroundColorOptions: BACKGROUND_COLOR_TOKENS,
      backgroundOptions: ['none', 'image', 'color'].map((bgType) => ({
        label: this.t(`background.${bgType}`),
        value: bgType,
      })),
      foregroundMediaOptions: ['none', 'image', 'video'].map((type) => ({
        label: this.t(`foreground.${type}`),
        value: type,
      })),
    }
  },
  computed: {
    ...mapGetters({
      blockById: 'websiteBuilder/blockById',
    }),
    currentBlockParams() {
      const { id } = this

      return this.blockById(id)?.params
    },
    foregroundMedia() {
      return this.currentBlockParams.foreground ?? this.currentBlockParams.media
    },
  },
  beforeMount() {
    const { currentBlockParams } = this

    this.setInitialFormValues({
      body: currentBlockParams.body,
      header: currentBlockParams.header,
    })
  },
  methods: {
    async applyActionOverride(newValue) {
      const { id } = this

      const overrideAction =
        ContentCommands.overrideBlockContent('params.action')

      await overrideAction({
        targetId: id,
        update: newValue,
      })
    },
    async applyContentUpdate(newValue) {
      const { id } = this

      await ContentCommands.updateBlock({
        targetId: id,
        update: {
          params: {
            ...newValue,
          },
        },
      })
    },
    async changeBackgroundType(type) {
      if (type === 'image') {
        await this.applyContentUpdate({
          background: { params: { image: '' }, type: 'image' },
        })
      } else if (type === 'color') {
        // default to a safe token
        await this.applyContentUpdate({
          background: { params: { token: 'white' }, type: 'color' },
        })
      } else {
        await this.applyContentUpdate({ background: null })
      }
    },
    async changeMediaType(mediaType) {
      if (mediaType === 'image') {
        const next = { params: { image: '' }, type: 'image' }

        await this.applyContentUpdate({
          alignment: 'left',
          foreground: next,
          media: next,
        })
      } else if (mediaType === 'video') {
        const nextValue = {
          params: {
            params: {
              id: '',
            },
            type: 'youtube',
          },
          type: 'video',
        }

        await this.applyContentUpdate({
          alignment: 'left',
          foreground: nextValue,
          media: nextValue,
        })
      } else {
        await this.applyContentUpdate({
          alignment: 'center',
          foreground: null,
          media: null,
        })
      }
    },
    async onBackgroundImageUpdate({ controlName, value, status }) {
      if (!status.valid) return
      if (controlName !== 'backgroundImage') return

      await this.applyContentUpdate({
        background: {
          params: { image: value },
          type: 'image',
        },
      })
    },
    async onFormControlUpdate(controlName, value, status) {
      if (!status.valid) {
        return
      }

      let update
      if (controlName === 'foregroundImage') {
        const nextPayload = {
          params: {
            image: value,
          },
          type: 'image',
        }

        update = {
          foreground: nextPayload,
          media: nextPayload,
        }
      } else if (controlName === 'video') {
        const nextPayload = {
          params: {
            params: {
              id: value,
            },
            type: 'youtube',
          },
          type: 'video',
        }

        update = {
          foreground: nextPayload,
          media: nextPayload,
        }
      } else {
        update = { [controlName]: value }
      }
      await this.applyContentUpdate(update)
    },
    async toggleAction(enabled) {
      if (enabled === true) {
        await this.applyContentUpdate({
          action: {
            params: { label: 'Enter title', url: '' },
            type: 'external',
          },
        })
      } else {
        await this.applyContentUpdate({ action: null })
      }
    },
    tokenToCss(token) {
      return tokenToCss(token, this.mergedTheme)
    },
    async updateBackgroundColor(token) {
      await this.applyContentUpdate({
        background: {
          params: { token },
          type: 'color',
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.content-editor {
  &__action-inputs {
    margin-top: 24px;
  }

  &__color-tiles {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  &__color-tile {
    width: 54px;
    height: 54px;
    display: inline-block;
    border-radius: 12px;
    border: 1px solid var(--divider);
    cursor: pointer;
  }

  &__color-tile--selected {
    outline: 2px solid var(--misc-brand);
    outline-offset: 2px;
  }
}
</style>
